import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { MapPin, Star, DollarSign, Building, Clock, ExternalLink, Store, Hash } from 'lucide-react';
import type { Venue } from '../../types';

interface VenueDetailsProps {
  venue: Venue;
}

export function VenueDetails({ venue }: VenueDetailsProps) {
  const formatOperatingHours = () => {
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    return days.map(day => {
      const hours = venue.operating_hours[day as keyof typeof venue.operating_hours];
      return {
        day,
        hours: hours.isOpen ? `${hours.from_hour} - ${hours.to_hour}` : 'Closed',
        isOpen: hours.isOpen
      };
    });
  };

  return (
    <div className="space-y-3">
      {/* Venue Name Card with Badge */}
      <Card className="border-primary/20 bg-gradient-to-br from-primary/5 to-primary/10">
        <CardContent className="py-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="rounded bg-primary/10 p-1">
                <Store className="h-4 w-4 text-primary" />
              </div>
              <div>
                <p className="text-xs font-medium text-muted-foreground">Venue</p>
                <p className="text-xl font-bold tracking-wide text-primary">
                  {venue.name}
                </p>
              </div>
            </div>
            <Badge variant="secondary" className="px-2 py-0.5 text-xs">
              {venue.primary_type}
            </Badge>
          </div>
        </CardContent>
      </Card>

      {/* Location Information */}
      <Card>
        <CardHeader className="pb-1 pt-2">
          <CardTitle className="flex items-center gap-2 text-sm font-semibold">
            <MapPin className="h-3.5 w-3.5 text-muted-foreground" />
            Location Information
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-1 pb-2">
          <div className="py-1">
            <p className="text-xs text-muted-foreground mb-0.5">Address</p>
            <p className="text-sm">{venue.formatted_address}</p>
            <a
              href={venue.google_maps_uri}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-xs text-primary hover:underline mt-1"
            >
              View on Google Maps
              <ExternalLink className="h-3 w-3" />
            </a>
          </div>
          <div className="flex justify-between items-center py-1 border-t">
            <span className="text-xs text-muted-foreground">DMA Region</span>
            <span className="text-sm font-medium">{venue.dma}</span>
          </div>
        </CardContent>
      </Card>

      {/* Venue Details */}
      <Card>
        <CardHeader className="pb-1 pt-2">
          <CardTitle className="flex items-center gap-2 text-sm font-semibold">
            <Hash className="h-3.5 w-3.5 text-muted-foreground" />
            Venue Details
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-1 pb-2">
          {venue.rating > 0 && (
            <div className="flex justify-between items-center py-1 border-b">
              <span className="text-xs text-muted-foreground flex items-center gap-1">
                <Star className="h-3 w-3 text-yellow-500 fill-yellow-500" />
                Rating
              </span>
              <div className="text-sm font-medium">
                {venue.rating.toFixed(1)} 
                <span className="text-xs text-muted-foreground ml-1">
                  ({venue.user_rating_count} reviews)
                </span>
              </div>
            </div>
          )}
          {venue.price_range && (
            <div className="flex justify-between items-center py-1 border-b">
              <span className="text-xs text-muted-foreground flex items-center gap-1">
                <DollarSign className="h-3 w-3" />
                Price Range
              </span>
              <span className="text-sm font-medium">{venue.price_range}</span>
            </div>
          )}
          {venue.organization && (
            <div className="flex justify-between items-center py-1">
              <span className="text-xs text-muted-foreground flex items-center gap-1">
                <Building className="h-3 w-3" />
                Organization
              </span>
              <span className="text-sm font-medium">{venue.organization.name}</span>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Operating Hours */}
      <Card>
        <CardHeader className="pb-1 pt-2">
          <CardTitle className="flex items-center gap-2 text-sm font-semibold">
            <Clock className="h-3.5 w-3.5 text-muted-foreground" />
            Operating Hours
          </CardTitle>
        </CardHeader>
        <CardContent className="pb-2">
          <div className="space-y-0.5">
            {formatOperatingHours().map(({ day, hours, isOpen }) => (
              <div key={day} className="flex justify-between items-center py-1 text-xs border-b last:border-0">
                <span className="font-medium">{day}</span>
                <span className={!isOpen ? 'text-muted-foreground' : 'font-medium'}>
                  {hours}
                </span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}